/**
 * Pure state machine for one signing step. No storage, no React, no network —
 * everything here is a total function so the whole transition table is testable.
 *
 * Vocabulary deliberately mirrors the step model already in CreatePoolForm.jsx
 * (`idle | simulating | pending | confirming | success | failed`) so that file
 * barely changes when it migrates:
 *
 *   building           ≈ simulating
 *   awaiting_signature ≈ pending      (wallet prompt is open)
 *   submitted          ≈ confirming   (hash known, waiting for a receipt)
 *   confirmed/done     ≈ success
 *   rejected|reverted|dropped|replaced|expired ≈ failed
 *
 * Note there is no separate `mining` state. wagmi gives us exactly two
 * observable facts — "we have a hash" and "we have a receipt" — so a state
 * between them would carry no information and could never be asserted in a
 * test. `submitted` covers the whole mempool period.
 */

import { serializeError } from "./codec";
import { effectKeyFor } from "./keys";

export const STATES = {
  IDLE: "idle",
  BUILDING: "building",
  AWAITING_SIGNATURE: "awaiting_signature",
  SUBMITTED: "submitted",
  CONFIRMED: "confirmed",
  EFFECTS_PENDING: "effects_pending",
  DONE: "done",
  // terminal failures
  REJECTED: "rejected", // user denied in the wallet — no transaction exists
  REVERTED: "reverted", // mined with status 0x0 — gas was spent
  DROPPED: "dropped", // hash never mined and the nonce moved past it
  REPLACED: "replaced", // superseded by a different tx at the same nonce
  EXPIRED: "expired", // too old to still be resolvable
};

export const EVENTS = {
  BUILD: "BUILD",
  SIGN_REQUESTED: "SIGN_REQUESTED",
  NONCE_CAPTURED: "NONCE_CAPTURED",
  HASH_RECEIVED: "HASH_RECEIVED",
  RECEIPT: "RECEIPT",
  USER_REJECTED: "USER_REJECTED",
  SEND_FAILED: "SEND_FAILED",
  DROPPED: "DROPPED",
  REPLACED: "REPLACED",
  EFFECT_INTENT: "EFFECT_INTENT",
  EFFECT_REQUIRED: "EFFECT_REQUIRED",
  EFFECT_SETTLED: "EFFECT_SETTLED",
  EFFECT_FAILED: "EFFECT_FAILED",
  EXPIRE: "EXPIRE",
  RESET: "RESET",
};

const TERMINAL = new Set([
  STATES.DONE,
  STATES.REJECTED,
  STATES.REVERTED,
  STATES.DROPPED,
  STATES.REPLACED,
  STATES.EXPIRED,
]);

/** Terminal states never transition again (except via RESET). */
export function isTerminal(status) {
  return TERMINAL.has(status);
}

/**
 * "Open" = the recovery scanner still owes this row an answer. Anything not
 * terminal, and not merely idle/building (which never produced a wallet prompt
 * and therefore cannot have produced a transaction).
 */
export function isOpen(entry) {
  if (!entry) return false;
  if (isTerminal(entry.status)) return false;
  return (
    entry.status === STATES.AWAITING_SIGNATURE ||
    entry.status === STATES.SUBMITTED ||
    entry.status === STATES.CONFIRMED ||
    entry.status === STATES.EFFECTS_PENDING
  );
}

/**
 * Is a wallet prompt open, or a transaction actually mining?
 *
 * The distinction that matters when the user starts a NEW attempt on the same
 * step: an in-flight row must be left alone (its spinner is telling the truth),
 * while anything else — confirmed, effects_pending, or any terminal — is residue
 * from a finished attempt and must be superseded. `confirmed` counts as residue:
 * it is settled for a step with no effect, and `transition()` ignores
 * SIGN_REQUESTED there just as it does on a terminal row.
 */
export function isInFlight(status) {
  return (
    status === STATES.BUILDING ||
    status === STATES.AWAITING_SIGNATURE ||
    status === STATES.SUBMITTED
  );
}

/**
 * The row holds the OUTCOME of an attempt — success or failure — rather than
 * an attempt still in progress.
 *
 * Two callers, same question:
 *
 *  - a new signing attempt must RESET such a row, or its SIGN_REQUESTED is
 *    swallowed and the hook keeps projecting the old outcome;
 *  - a component mounting onto such a row must NOT be told "success", because
 *    it never witnessed the transition. Reporting it anyway reopened the "Funds
 *    are UnStaked successfully" modal every time the user navigated back to the
 *    Stake page.
 */
export function isSettled(status) {
  return status !== STATES.IDLE && !isInFlight(status);
}

/** @deprecated name kept for the write hook's call site; same predicate. */
export function needsResetBeforeNewAttempt(status) {
  return isSettled(status);
}

/** Did this step reach the chain successfully? */
export function isSuccessful(status) {
  return (
    status === STATES.CONFIRMED ||
    status === STATES.EFFECTS_PENDING ||
    status === STATES.DONE
  );
}

export function isFailure(status) {
  return (
    status === STATES.REJECTED ||
    status === STATES.REVERTED ||
    status === STATES.DROPPED ||
    status === STATES.REPLACED ||
    status === STATES.EXPIRED
  );
}

const ALLOWED = {
  [STATES.IDLE]: [STATES.BUILDING, STATES.AWAITING_SIGNATURE],
  [STATES.BUILDING]: [STATES.AWAITING_SIGNATURE, STATES.REJECTED, STATES.IDLE],
  [STATES.AWAITING_SIGNATURE]: [
    STATES.SUBMITTED,
    STATES.REJECTED,
    STATES.DROPPED,
    STATES.REPLACED,
    STATES.EXPIRED,
    // A smart-account flow can be resolved straight to confirmed by the
    // verifier without us ever seeing a hash.
    STATES.CONFIRMED,
  ],
  [STATES.SUBMITTED]: [
    STATES.SUBMITTED, // speed-up: new hash, same step
    STATES.CONFIRMED,
    STATES.REVERTED,
    STATES.DROPPED,
    STATES.REPLACED,
    STATES.EXPIRED,
  ],
  [STATES.CONFIRMED]: [STATES.EFFECTS_PENDING, STATES.DONE],
  [STATES.EFFECTS_PENDING]: [STATES.DONE, STATES.EFFECTS_PENDING],
  [STATES.DONE]: [],
  [STATES.REJECTED]: [],
  [STATES.REVERTED]: [],
  [STATES.DROPPED]: [],
  [STATES.REPLACED]: [],
  [STATES.EXPIRED]: [],
};

export function canTransition(from, to) {
  if (from === to && from === STATES.SUBMITTED) return true;
  const allowed = ALLOWED[from];
  return Array.isArray(allowed) && allowed.includes(to);
}

function withBase(entry, patch, now) {
  return { ...entry, ...patch, updatedAt: now };
}

/**
 * Apply an event. Returns a NEW entry, or the same reference when the event is
 * not applicable (late/duplicate events are ignored rather than throwing —
 * recovery and the live hooks can both observe the same receipt).
 *
 * @param {object} entry current journal row
 * @param {string} event one of EVENTS
 * @param {object} [payload]
 * @param {number} [now] injectable clock for deterministic tests
 */
export function transition(entry, event, payload = {}, now = Date.now()) {
  if (!entry) return entry;

  // Terminal rows only respond to RESET. This is what makes duplicate events
  // from two observers (live hook + recovery scanner) harmless.
  if (isTerminal(entry.status) && event !== EVENTS.RESET) return entry;

  switch (event) {
    case EVENTS.BUILD:
      if (!canTransition(entry.status, STATES.BUILDING)) return entry;
      return withBase(entry, { status: STATES.BUILDING }, now);

    case EVENTS.SIGN_REQUESTED: {
      if (!canTransition(entry.status, STATES.AWAITING_SIGNATURE)) return entry;
      return withBase(
        entry,
        {
          status: STATES.AWAITING_SIGNATURE,
          // Captured BEFORE the wallet prompt — this is what makes a
          // reload-during-signature recoverable at all.
          nonceAtSubmit:
            payload.nonce === undefined ? entry.nonceAtSubmit : payload.nonce,
          request: payload.request || entry.request,
          error: null,
        },
        now
      );
    }

    /**
     * The nonce arrives slightly AFTER the wallet prompt opens.
     *
     * Fetching it first would add an RPC round trip in front of every
     * MetaMask popup — a visible behaviour change. Instead the row is written
     * synchronously (so a reload during signing is already recoverable via the
     * flow's on-chain verifier) and the nonce is patched in when it lands,
     * upgrading recovery to the cheaper nonce path.
     *
     * Only ever fills a blank: once a nonce is recorded it is never revised,
     * and a late arrival after the hash is ignored.
     */
    case EVENTS.NONCE_CAPTURED: {
      if (entry.status !== STATES.AWAITING_SIGNATURE) return entry;
      if (entry.nonceAtSubmit != null) return entry;
      if (payload.nonce == null) return entry;
      return withBase(entry, { nonceAtSubmit: payload.nonce }, now);
    }

    case EVENTS.HASH_RECEIVED: {
      const hash = payload.hash;
      if (!hash) return entry;
      if (!canTransition(entry.status, STATES.SUBMITTED)) return entry;
      const hashes = entry.hashes.includes(hash)
        ? entry.hashes
        : [...entry.hashes, hash];
      // Duplicate of the hash we already have and already submitted: no-op, so
      // React does not re-render on every poll.
      if (hashes === entry.hashes && entry.status === STATES.SUBMITTED) return entry;
      return withBase(entry, { status: STATES.SUBMITTED, hashes, error: null }, now);
    }

    /**
     * Declare the post-confirmation effect UP FRONT, at signing time.
     *
     * This has to happen before the transaction confirms, because the payload
     * is built from component state — selected currency, bank, amount — and
     * after a reload that state is gone. Journaling it here is what lets the
     * effect still be delivered by a session that never saw the form.
     *
     * It stays inert (`status: "intent"`) until the receipt arrives, so a
     * reverted or rejected transaction never fires anything.
     */
    case EVENTS.EFFECT_INTENT: {
      if (entry.status !== STATES.AWAITING_SIGNATURE && entry.status !== STATES.SUBMITTED) {
        return entry;
      }
      if (!payload.type) return entry;
      return withBase(
        entry,
        {
          effect: {
            type: payload.type,
            payload: payload.payload,
            effectKey: null, // derived from the tx hash once we have one
            status: "intent",
            attempts: 0,
            lastError: null,
          },
        },
        now
      );
    }

    case EVENTS.RECEIPT: {
      const receipt = payload.receipt;
      if (!receipt) return entry;
      const ok = receipt.status === "success" || receipt.status === 1 || receipt.status === "0x1";
      const target = ok ? STATES.CONFIRMED : STATES.REVERTED;
      if (!canTransition(entry.status, target)) return entry;
      const hash = receipt.transactionHash;
      const hashes = hash && !entry.hashes.includes(hash) ? [...entry.hashes, hash] : entry.hashes;
      const resolvedHash = hash || entry.hashes[entry.hashes.length - 1] || null;

      // A declared intent arms itself the moment the transaction succeeds, and
      // only then. The key is derived from the hash so a replay from any tab or
      // session produces the same idempotency key.
      const arming =
        ok && entry.effect && entry.effect.status === "intent"
          ? {
              status: STATES.EFFECTS_PENDING,
              effect: {
                ...entry.effect,
                status: "pending",
                effectKey: effectKeyFor(entry.effect.type, resolvedHash),
              },
            }
          : { status: target };

      return withBase(
        entry,
        {
          ...arming,
          hashes,
          receipt: {
            transactionHash: resolvedHash,
            blockNumber:
              typeof receipt.blockNumber === "bigint"
                ? receipt.blockNumber.toString()
                : receipt.blockNumber ?? null,
            status: ok ? "success" : "reverted",
          },
        },
        now
      );
    }

    case EVENTS.USER_REJECTED:
      if (!canTransition(entry.status, STATES.REJECTED)) return entry;
      return withBase(
        entry,
        { status: STATES.REJECTED, error: serializeError(payload.error) },
        now
      );

    case EVENTS.SEND_FAILED: {
      // A send failure with no hash is indistinguishable from a rejection for
      // our purposes: nothing reached the chain.
      if (!canTransition(entry.status, STATES.REJECTED)) return entry;
      return withBase(
        entry,
        { status: STATES.REJECTED, error: serializeError(payload.error) },
        now
      );
    }

    case EVENTS.DROPPED:
      if (!canTransition(entry.status, STATES.DROPPED)) return entry;
      return withBase(entry, { status: STATES.DROPPED }, now);

    case EVENTS.REPLACED:
      if (!canTransition(entry.status, STATES.REPLACED)) return entry;
      return withBase(entry, { status: STATES.REPLACED }, now);

    case EVENTS.EFFECT_REQUIRED: {
      if (!canTransition(entry.status, STATES.EFFECTS_PENDING)) return entry;
      return withBase(
        entry,
        {
          status: STATES.EFFECTS_PENDING,
          effect: {
            ...(entry.effect || {}),
            type: payload.type,
            payload: payload.payload,
            effectKey: payload.effectKey,
            status: "pending",
            attempts: entry.effect?.attempts || 0,
            lastError: null,
          },
        },
        now
      );
    }

    case EVENTS.EFFECT_SETTLED: {
      if (!canTransition(entry.status, STATES.DONE)) return entry;
      return withBase(
        entry,
        {
          status: STATES.DONE,
          effect: entry.effect
            ? { ...entry.effect, status: payload.applied || "applied", lastError: null }
            : entry.effect,
        },
        now
      );
    }

    case EVENTS.EFFECT_FAILED: {
      if (entry.status !== STATES.EFFECTS_PENDING) return entry;
      return withBase(
        entry,
        {
          effect: {
            ...(entry.effect || {}),
            status: "retrying",
            attempts: (entry.effect?.attempts || 0) + 1,
            lastError: serializeError(payload.error),
          },
        },
        now
      );
    }

    case EVENTS.EXPIRE:
      if (!canTransition(entry.status, STATES.EXPIRED)) return entry;
      return withBase(entry, { status: STATES.EXPIRED }, now);

    case EVENTS.RESET:
      return withBase(
        entry,
        {
          status: STATES.IDLE,
          hashes: [],
          receipt: null,
          error: null,
          effect: null,
          nonceAtSubmit: null,
        },
        now
      );

    default:
      return entry;
  }
}

/**
 * Project a journal row onto the exact shape the wagmi hooks return, so screens
 * can keep destructuring `{ data, isPending, error }` and `{ isLoading, isSuccess }`
 * without a single JSX change. This is the "no UI change" contract in code.
 */
export function toWriteShape(entry) {
  if (!entry) {
    return { data: undefined, isPending: false, isError: false, error: null, status: "idle" };
  }
  const latestHash = entry.hashes[entry.hashes.length - 1];
  return {
    // wagmi's `data` for useWriteContract is the tx hash.
    data: latestHash,
    // `isPending` in wagmi means "the wallet request is in flight", i.e. before
    // a hash exists. It must NOT stay true once we have a hash.
    isPending:
      entry.status === STATES.BUILDING || entry.status === STATES.AWAITING_SIGNATURE,
    isError: isFailure(entry.status) && entry.status !== STATES.EXPIRED,
    error: entry.error || null,
    status: entry.status,
  };
}

export function toReceiptShape(entry) {
  if (!entry) {
    return { data: undefined, isLoading: false, isSuccess: false, isError: false, error: null };
  }
  return {
    data: entry.receipt || undefined,
    isLoading: entry.status === STATES.SUBMITTED,
    isSuccess: isSuccessful(entry.status),
    isError: entry.status === STATES.REVERTED,
    error: entry.error || null,
  };
}
